import { Box, Stack, alpha, useTheme } from "@mui/material";
import AddressFeasibilityPanel from "./AddressFeasibilityPanel";
import JerryPill from "./JerryPill";
import StepHeader from "./StepHeader";
import AddressAutocomplete from "src/components/AddressAutocomplete";
import { useT } from "src/i18n";

export default function StepAddress({
    draft, errors, onChange,
    checkStatus = "idle", checkError = null, onRetry,
    onAddressPicked,
}) {
    const theme = useTheme();
    const w = theme.custom?.wizard ?? {};
    const t = useT();

    function onAddressChange(value) {
        onChange({
            osoite: value,
            feasibility: null,
            lat: null, lng: null,
            placeId: null, formattedAddress: null,
        });
    }

    async function handlePick({ lat, lng, formattedAddress, placeId, description }) {
        const finalAddress = formattedAddress || description;
        if (onAddressPicked) {
            await onAddressPicked({
                osoite: description,
                formattedAddress: finalAddress,
                placeId,
                lat,
                lng,
            });
        } else {
            onChange({
                osoite: description,
                formattedAddress: finalAddress,
                placeId,
                lat,
                lng,
                feasibility: null,
            });
        }
    }

    return (
        <Stack spacing={2}>
            <JerryPill message={t("wizard.stepAddress.jerryMessage")} />
            <StepHeader>{t("wizard.stepAddress.title")}</StepHeader>
            <Box sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "1rem",
                color: "text.secondary",
                maxWidth: "60ch",
                lineHeight: 1.55,
            }}>
                {t("wizard.stepAddress.intro")}
            </Box>
            <AddressAutocomplete
                value={draft.osoite || ""}
                onValueChange={onAddressChange}
                onPick={handlePick}
                label={t("wizard.stepAddress.addressLabel")}
                required
                error={!!errors.osoite}
                helperText={errors.osoite ? t(errors.osoite) : t("wizard.stepAddress.addressHelper")}
                captureErrorTags={{ entity: "pumppuRequest", operation: "fetchPlaceDetails" }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: w.cardBgFallback ?? "background.paper",
                            border: w.cardBorder ?? undefined,
                            borderRadius: `${w.fieldRadius ?? 10}px`,
                            boxShadow: w.cardShadow,
                            "@supports (backdrop-filter: blur(20px)) or (-webkit-backdrop-filter: blur(20px))": {
                                bgcolor: w.cardBg ?? undefined,
                                backdropFilter: w.cardBlur ?? undefined,
                                WebkitBackdropFilter: w.cardBlur ?? undefined,
                            },
                            "& .MuiAutocomplete-option": {
                                transition: `background-color ${w.motion?.focus ?? "160ms"} ${w.motion?.easing ?? "ease"}`,
                            },
                            "& .MuiAutocomplete-option.Mui-focused": {
                                backgroundColor: w.amber ? alpha(w.amber, 0.16) : undefined,
                            },
                        },
                    },
                }}
            />
            <AddressFeasibilityPanel
                status={checkStatus}
                feasibility={draft.feasibility}
                error={checkError}
                onRetry={onRetry}
            />
        </Stack>
    );
}
